import type { SortEnum, WorksheetSubject } from "@arbeidsark/shared"
import truncate from "lodash/truncate"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import styled, { useTheme } from "styled-components"
import { Button, Icon, QuoTable, SVGIcon, TableRowMenu } from "~/components"
import { captureEvent } from "~/modules/analytics/capture"
import { useStore } from "~/store"
import { useDebounce, useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { format } from "~/utils/date-fns/format"
import { useMedia } from "~/utils/hooks/useMedia"
import { trpc } from "~/utils/trpc"
import ShareLinkModal from "./components/ShareLinkModal"
import { downloadWorksheet, printPDF } from "./util"

const Layout = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  width: calc(100% - 64px);

  margin: 16px 24px;

  ${(props) => props.theme.media.pad} {
    margin: 16px 0;
    width: 100%;
  }
`

const TableTitle = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 0 12px;

  width: 100%;
  background-color: ${(props) => props.theme.colors.gray99};

  span {
    line-height: 29px;
    color: ${(props) => props.theme.colors.gray0};
    font-weight: 500;
    font-size: 21px;
    align-self: flex-start;
    font-family: 'Work Sans', sans-serif;
    margin-bottom: 5.5px;
  }

  ${(props) => props.theme.media.pad} {
    width: calc(100% - 64px);
    padding: 0 32px 12px;
  }

  ${(props) => props.theme.media.mobile} {
    padding: 0 16px 12px;
  }
`

const TableWrapper = styled.div`
  width: 100%;

  ${(props) => props.theme.media.mobile} {
    padding-bottom: 4rem;
  }
`

const RowMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  font-size: 16px;
  height: 100%;
  color: ${(props) => props.theme.colors.gray60};
  justify-content: space-between;
  margin-left: 16px;

  ${(props) => props.theme.media.mobile} {
    margin: 0;
    justify-content: end;
  }
`

// To give the icons some range to be clicked. Currently, requires a user to be atleast doublAK in CS:GO to click icons in a reasonable time.
const RowMenuIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 32px;
  border-radius: 8px;

  :hover {
    cursor: pointer;
    background-color: ${(props) => props.theme.colors.primary95};
  }

  ${(props) => props.theme.media.mobile} {
    padding: 10px;
  }
`

const Name = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: 0;
  text-align: left;
  cursor: pointer;
`

const Subject = styled(Name)`
  font-weight: 400;
  color: ${(props) => props.theme.colors.gray50};
  width: 200px;
`

const CombinedCell = styled.div`
  display: flex;
  flex-direction: column;
  padding: 14px;
  justify-content: space-between;
  height: calc(72px - 28px);
  cursor: pointer;

  ${(props) => props.theme.media.mobile} {
    padding: 0 8px;
  }
`

const StarWrapper = styled.div`
  :hover {
    cursor: pointer;
  }

  padding-right: 16px;
`

const TabWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  border-bottom: 2px solid ${(props) => props.theme.colors.gray95};

  :hover {
    cursor: pointer;
  }
`

interface TabProps {
  borderTopRight?: boolean
  borderTopLeft?: boolean
  active?: boolean
}

const Tab = styled.div<TabProps>`
  display: flex;
  justify-content: center;
  padding: 14px;

  color: ${(props) => props.theme.colors.gray60};
  background: ${(props) => props.theme.colors.gray95};

  ${(props) => props.borderTopLeft && "border-top-left-radius: 8px;"}
  ${(props) => props.borderTopRight && "border-top-right-radius: 8px;"}

    ${(props) =>
      props.active &&
      `
  
  background: white;
  color: ${props.theme.colors.primary40};
  `}
`

const MobileButtonWrapper = styled.div`
  position: fixed;
  bottom: 12px;
  display: flex;
  width: 100%;
  justify-content: center;

  button {
    width: 300px;
    height: 48px;
  }
`

type MyWorksheetsProps = Record<string, never>

type WorksheetRowData = {
  id?: number
  name?: string
  subject?: WorksheetSubject
  grade?: number | null
  isFavourite?: boolean
}

export const MyWorksheets: React.FC<MyWorksheetsProps> = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { mobile, pad } = useMedia()
  const addToast = useToast()

  const [sort, setSort] = useState<Record<string, SortEnum>>({})
  const [subjectFilter, setSubjectFilter] = useState<WorksheetSubject[]>([])
  const [printing, setPrinting] = useState(false)
  const [onlyShowFavourites, setOnlyShowFavourites] = useState<boolean>(false)

  const searchWorksheets = useStore((state) => state.header.searchWorksheets)
  const debouncedSearch = useDebounce(searchWorksheets)

  useEffect(() => {
    if (debouncedSearch === "" || debouncedSearch === undefined) return
    captureEvent("search_worksheets", { search: debouncedSearch })
  }, [debouncedSearch])

  // If this is non-null we display a modal with the link for autocopying
  const [worksheetShareLink, setWorksheetShareLink] = useState<string | null>(
    "",
  )

  const translations = useTranslate({
    createWorksheet: "common.create-worksheet",
    welcomeTextMyWorksheets: "dashboard.welcome-text-my-worksheets",
    welcomeTextTwo: "dashboard.welcome-text-two",
    myWorksheets: "common.my-worksheets",

    name: "common.name",
    subject: "common.subject",
    date: "common.date",

    mathematics: "common.mathematics",
    norwegian: "common.norwegian",
    english: "common.english",

    showAll: "common.show-all",
    favourites: "common.favourites",
    menu: {
      print: "common.print",
      download: "common.download",
      favourite: "common.favourite",
      unfavourite: "common.unfavourite",
      share: "common.share",
      delete: "common.delete-alt",

      toast: {
        deleteSuccess: "worksheets.delete-success",
      },
    },
  })

  const trpcUtils = trpc.useUtils()

  const { data, isLoading } = trpc.worksheets.getMyWorksheets.useQuery({
    filterString: debouncedSearch,
    subject: subjectFilter,
    isFavourite: onlyShowFavourites ? true : undefined,
    orderBy: Object.keys(sort).map((key) => ({
      [key]: sort[key],
    })),
  })

  const { mutate: setFavourite } = trpc.worksheets.setFavourite.useMutation({
    onSuccess(_input) {
      trpcUtils.worksheets.getMyWorksheets.invalidate()
    },
  })

  const { mutate: deleteWorksheet } = trpc.worksheets.deleteById.useMutation({
    onSuccess(_input) {
      trpcUtils.worksheets.getMyWorksheets.invalidate()
      addToast("success", translations.menu.toast.deleteSuccess)
    },
  })

  const { mutate: createWorksheetShareLink } =
    trpc.share.getShareableLink.useMutation({
      onSuccess(input) {
        setWorksheetShareLink(input.url)
      },
    })

  // TODO: Consider improving subject typing here
  const getSubjectTranslation = (subject: string) => {
    if (subject === "NORWEGIAN") {
      return translations.norwegian
    } else if (subject === "ENGLISH") {
      return translations.english
    } else if (subject === "MATHEMATICS") {
      return translations.mathematics
    }
    return subject
  }

  const columns = [
    {
      id: "favourited",
      label: "",
      sortable: false,
      hide: mobile,
      width: "16px",
    },
    {
      id: "name",
      label: translations.name,
      sortable: true,
      sortKey: "name",
      width: "40%",
    },
    {
      id: "subject",
      label: translations.subject,
      sortable: false,
      filterable: true,
      filterValues: [
        { value: "MATHEMATICS", label: translations.mathematics },
        { value: "NORWEGIAN", label: translations.norwegian },
        { value: "ENGLISH", label: translations.english },
      ],
      sortKey: "subject",
      hide: mobile,
      width: "20%",
    },
    {
      id: "date",
      label: translations.date,
      sortable: true,
      sortKey: "createdAt",
      hide: mobile,
      width: "20%",
    },
    {
      id: "rowMenu",
      label: pad ? (
        ""
      ) : (
        <Button
          size="small"
          width="224px"
          leftIcon="plus"
          onClick={() => navigate("/generator/mathematics")}
        >
          {translations.createWorksheet}
        </Button>
      ),
      width: mobile ? "16px" : "204px",
    },
  ]

  // function openWorksheetPDF(file_url: string) {
  //   window.open(file_url, '_blank')
  // }

  function printWorksheet(id: number) {
    if (printing) return
    setPrinting(true)
    printPDF(id)
      .catch((err) => {
        addToast("error", err)
      })
      .finally(() => setPrinting(false))
  }

  function downloadWorksheetFromLibrary(
    worksheet: WorksheetRowData,
    source: "inline" | "menu",
  ) {
    if (
      typeof worksheet.id !== "number" ||
      typeof worksheet.name !== "string" ||
      !worksheet.subject
    ) {
      return
    }

    void downloadWorksheet(worksheet.id, worksheet.name, {
      subject: worksheet.subject,
      grade: worksheet.grade ?? null,
      source: `my_worksheets_${source}`,
    })
  }

  function rowMenuCell(worksheet: WorksheetRowData) {
    if (typeof worksheet.id !== "number") {
      return null
    }

    const isFavourite = !!worksheet.isFavourite

    return (
      <RowMenuWrapper>
        {!pad && [
          <RowMenuIconWrapper
            key={"download-inline"}
            onClick={() => {
              downloadWorksheetFromLibrary(worksheet, "inline")
            }}
          >
            <Icon icon="download" />
          </RowMenuIconWrapper>,
          <RowMenuIconWrapper
            key={"print-inline"}
            onClick={() => {
              printWorksheet(worksheet.id)
            }}
          >
            <Icon icon="print" />
          </RowMenuIconWrapper>,
          <RowMenuIconWrapper
            key={"share-inline"}
            onClick={() =>
              createWorksheetShareLink({ worksheetId: worksheet.id })
            }
          >
            <SVGIcon width="16px" height="16px" icon="ShareNodes" />
          </RowMenuIconWrapper>,
        ]}
        <TableRowMenu
          items={[
            {
              label: translations.menu.print,
              icon: "Print",
              onClick: () => {
                printWorksheet(worksheet.id)
              },
            },
            {
              label: translations.menu.download,
              icon: "Download",
              onClick: () => {
                downloadWorksheetFromLibrary(worksheet, "menu")
              },
            },
            {
              label: isFavourite
                ? translations.menu.unfavourite
                : translations.menu.favourite,
              icon: "Star",
              onClick: () => {
                setFavourite({
                  id: worksheet.id,
                  isFavourite: !isFavourite,
                })
              },
            },
            {
              label: translations.menu.share,
              icon: "ShareNodes",
              onClick: () => {
                createWorksheetShareLink({ worksheetId: worksheet.id })
              },
            },
            {
              label: translations.menu.delete,
              icon: "Exit",
              onClick: () => {
                deleteWorksheet({ id: worksheet.id })
              },
            },
          ]}
        />
      </RowMenuWrapper>
    )
  }

  const tableData = data?.map((worksheet) => ({
    id: worksheet.id.toString(),
    data: {
      ...worksheet,
      tag: worksheet.id,
      favourited: (
        <StarWrapper
          onClick={() => {
            setFavourite({
              id: worksheet.id,
              isFavourite: !worksheet.isFavourite,
            })
          }}
        >
          {worksheet.isFavourite ? (
            <Icon
              size="16px"
              icon="star"
              type="solid"
              color={theme.colors.warning60}
            />
          ) : (
            <Icon
              size="16px"
              icon="star"
              type="light"
              color={theme.colors.gray60}
            />
          )}
        </StarWrapper>
      ),
      name:
        mobile && worksheet.subject ? (
          <CombinedCell
            onClick={() => {
              //   openWorksheetPDF(worksheet.file)
            }}
          >
            <Name>{truncate(worksheet.name, { length: 38 })}</Name>
            <Subject>{`
                  ${getSubjectTranslation(worksheet.subject)} 
                  (${format(new Date(worksheet.createdAt), "dd. MMMM")})`}</Subject>
          </CombinedCell>
        ) : (
          <Name
            onClick={() => {
              // openWorksheetPDF(worksheet.file)
            }}
          >
            {worksheet.name}
          </Name>
        ),
      subject: <span>{getSubjectTranslation(worksheet.subject)}</span>,
      date: <span>{format(new Date(worksheet.createdAt), "dd. MMMM")}</span>,
      rowMenu: rowMenuCell(worksheet),
    },
  }))

  const handleFilterChanged = useCallback(
    (column: string, newValues: any[]) => {
      if (column !== "subject") return

      const values = newValues as WorksheetSubject[]

      setSubjectFilter(values)
    },
    [],
  )

  return (
    <Layout>
      <ShareLinkModal
        open={!!worksheetShareLink}
        shareLink={worksheetShareLink ?? ""}
        onClose={() => setWorksheetShareLink(null)}
      />
      {pad && (
        <TableTitle>
          <span>{translations.myWorksheets}</span>
          {!mobile && (
            <Button
              leftIcon="plus"
              size="small"
              width="220px"
              onClick={() => navigate("/generator/mathematics")}
            >
              {translations.createWorksheet}
            </Button>
          )}
        </TableTitle>
      )}
      <TableWrapper>
        <TabWrapper>
          <Tab
            borderTopLeft
            active={!onlyShowFavourites}
            onClick={() => setOnlyShowFavourites(false)}
          >
            {translations.showAll}
          </Tab>
          <Tab
            borderTopRight
            active={onlyShowFavourites}
            onClick={() => setOnlyShowFavourites(true)}
          >
            {translations.favourites}
          </Tab>
        </TabWrapper>

        <QuoTable
          columns={columns}
          data={tableData ?? []}
          sort={sort}
          filter={{ subject: subjectFilter }}
          headerProps={{
            fontSize: "12px",
            display: mobile ? "display: none;" : "",
            padding: "0 1rem 0 0",
          }}
          tableProps={{ fullWidth: true }}
          cellProps={{
            width: pad ? "134px" : "",
            padding: "0 1rem 0 0",
          }}
          loading={isLoading}
          loadingProps={{
            rows: 4,
            expectedWidths: [25, 100, 100, 100, 100],
          }}
          onSortChanged={setSort}
          onFilterChanged={handleFilterChanged}
        />
        {mobile && (
          <MobileButtonWrapper>
            <Button
              size="small"
              leftIcon="plus"
              onClick={() => navigate("/worksheetgenerator")}
            >
              {translations.createWorksheet}
            </Button>
          </MobileButtonWrapper>
        )}
      </TableWrapper>
    </Layout>
  )
}
